import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  MoreVertical, 
  UserCheck, 
  UserX, 
  Trash2,
  Filter,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ROLES } from '@/hooks/authentication/usePermissions';
import { useUserManagement } from '@/hooks/admin/useUserManagement';

/**
 * User Management Table Component - Integrated with Backend APIs
 */
export function UserManagementTable() {
  // Use the custom hook for all data and operations
  const {
    users: hookUsers,
    totalUsers: hookTotalUsers,
    availableRoles,
    isLoading,
    error,
    updateUserStatus: hookUpdateUserStatus,
    performBulkAction: hookPerformBulkAction,
    refreshData,
    updateFilters,
    clearError
  } = useUserManagement();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  // Use hook users or fallback to empty array
  const users = hookUsers || [];
  const totalUsers = hookTotalUsers || 0;

  // Update filters in hook when local filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateFilters({
        search: searchTerm,
        role: filterRole,
        status: filterStatus === 'active' ? 'ACTIVE' : filterStatus === 'inactive' ? 'DISABLED' : '',
        currentPage,
        pageSize
      });
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterRole, filterStatus, currentPage, pageSize, updateFilters]);

  // Handle sorting
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    if (!users || users.length === 0) return [];
    
    let filtered = [...users];

    // Sorting (client-side for current page)
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, sortConfig]);

  // Pagination - now handled by backend
  const totalPages = Math.ceil(totalUsers / pageSize);
  const paginatedUsers = filteredAndSortedUsers; // Already paginated from API

  // Selection handlers
  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedUsers(new Set(paginatedUsers.map(u => u.user_id)));
    } else {
      setSelectedUsers(new Set());
    }
  }, [paginatedUsers]);

  const handleSelectUser = useCallback((userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  // Status update handler
  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      await hookUpdateUserStatus(userId, !currentStatus);
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert(`Failed to update user status: ${error.message}`);
    }
  };

  // Bulk action handler
  const handleBulkAction = async (action) => {
    if (selectedUsers.size === 0) {
      alert('Please select users first');
      return;
    }

    const reason = action === 'DELETE' 
      ? prompt('Please provide a reason for deletion:')
      : null;

    if (action === 'DELETE' && !reason) {
      return; // Cancelled
    }

    try {
      const result = await hookPerformBulkAction(Array.from(selectedUsers), action, reason);
      setSelectedUsers(new Set());
      
      // Show success message
      const actionText = action === 'ACTIVATE' ? 'activated' : action === 'DEACTIVATE' ? 'deactivated' : 'deleted';
      alert(`${result.success_count} user(s) ${actionText} successfully. ${result.failed_count || 0} failed.`);
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert(`Bulk action failed: ${error.message}`);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:
        return 'bg-purple-100 text-purple-800';
      case ROLES.BUSINESS_ADMIN:
        return 'bg-blue-100 text-blue-800';
      case ROLES.OPERATOR:
        return 'bg-green-100 text-green-800';
      case ROLES.VIEWER:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshData();
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-800">{error.message}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <div className="text-sm text-gray-500">
              Total: {totalUsers} users
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by username, email, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="">All Roles</option>
              {availableRoles.map(role => (
                <option key={role.name} value={role.name}>{role.description}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.size > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('ACTIVATE')}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <UserCheck className="h-4 w-4" />
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('DEACTIVATE')}
                className="px-3 py-1 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-1"
              >
                <UserX className="h-4 w-4" />
                Deactivate
              </button>
              <button
                onClick={() => handleBulkAction('DELETE')}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto min-h-80">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2">
                  <input
                    type="checkbox"
                    checked={paginatedUsers.length > 0 && selectedUsers.size === paginatedUsers.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                </th>
                <th 
                  className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer"
                  onClick={() => handleSort('username')}
                >
                  Username {sortConfig.key === 'username' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer"
                  onClick={() => handleSort('role')}
                >
                  Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                  Status
                </th>
                <th 
                  className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer"
                  onClick={() => handleSort('last_login')}
                >
                  Last Login {sortConfig.key === 'last_login' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="py-3 px-2">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.user_id)}
                      onChange={() => handleSelectUser(user.user_id)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="py-3 text-sm font-medium text-gray-900">
                    {user.username}
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {user.email}
                  </td>
                  <td className="py-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                      {availableRoles.find(r => r.name === user.role)?.description || user.role}
                    </span>
                  </td>
                  <td className="py-3">
                    <button
                      onClick={() => handleStatusToggle(user.user_id, user.is_active)}
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-3 text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="py-3 text-right">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setActionMenuOpen(actionMenuOpen === user.user_id ? null : user.user_id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical className="h-4 w-4 text-gray-400" />
                      </button>
                      
                      {actionMenuOpen === user.user_id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                          <button
                            onClick={() => handleStatusToggle(user.user_id, user.is_active)}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            {user.is_active ? (
                              <>
                                <UserX className="h-4 w-4" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="h-4 w-4" />
                                Activate
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete user ${user.username}?`)) {
                                handleBulkAction('DELETE');
                              }
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || isLoading}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && paginatedUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Loading users...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default UserManagementTable;
